/**
 * What this organization has and what it is still missing, in the order the pieces have to arrive.
 *
 * **A list rather than a row of buttons.** Setting an organization up at Stripe is four steps that
 * depend on each other - a plan to record the customer on, a customer, a method if Stripe is to
 * charge one, and the subscription that actually raises the invoices - and a panel of buttons made
 * an operator work out the order for themselves, then left them unable to say what an organization
 * was missing without pressing something to find out.
 *
 * The next action is shown on the **first outstanding step only**. Every step naming what to do
 * next reads as four things to do now, and three of them would be refused.
 */

export type BillingSetupStepState =
  /** In place. */
  | 'done'
  /** Missing, and something later depends on it. */
  | 'todo'
  /**
   * Missing, and nothing is blocked by it. A payment method is this: Stripe emails the invoice
   * unless it is told otherwise, and that needs nothing on file.
   */
  | 'optional'
  /** Not known yet, because the read that would answer it has not come back. */
  | 'unknown';

export interface BillingSetupStep {
  /** Stable, and part of the test id. */
  key: string;
  name: string;
  state: BillingSetupStepState;
  /** What is true about this step right now, in a few words. */
  detail: string;
  /** What to do about it. Shown only when this is the first outstanding step. */
  action?: string;
}

interface BillingSetupChecklistProps {
  steps: BillingSetupStep[];
  /**
   * A sentence under the list, for the consequence that is not visible from any one step. Null
   * when there is nothing to warn about.
   */
  warning?: string | null;
}

/**
 * The state in words, never only in colour or an icon. The stylesheet tints these; the text is what
 * a screen reader reads and what survives a monochrome print.
 */
const STATE_LABEL: Record<BillingSetupStepState, string> = {
  done: 'Done',
  todo: 'Not yet',
  optional: 'Not needed',
  unknown: 'Checking',
};

export const BillingSetupChecklist = ({ steps, warning = null }: BillingSetupChecklistProps) => {
  const next = steps.find((step) => step.state === 'todo' && step.action);

  return (
    <div className="billing-checklist">
      <ol className="billing-checklist__list" data-testid="billing-setup-checklist">
        {steps.map((step) => (
          <li
            key={step.key}
            className={`billing-checklist__step billing-checklist__step--${step.state}`}
            data-testid={`billing-checklist-${step.key}`}
          >
            <span
              className="billing-checklist__state"
              data-testid={`billing-checklist-${step.key}-state`}
            >
              {STATE_LABEL[step.state]}
            </span>
            <span className="billing-checklist__body">
              <span className="billing-checklist__name">{step.name}</span>
              <span className="billing-checklist__detail">{step.detail}</span>
              {step.key === next?.key && (
                <span
                  className="billing-checklist__action"
                  data-testid={`billing-checklist-${step.key}-action`}
                >
                  Next: {step.action}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
      {warning && (
        <p className="billing-checklist__warning" data-testid="billing-setup-warning">
          {warning}
        </p>
      )}
    </div>
  );
};
